import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from '../i18n';

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function ChatInput({ value, onChange, onSend, isLoading, inputBarRef, textareaRef, onKeyDown, suggestedAnswers, onSuggestedAnswerClick }) {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const loadingRef = useRef(false);

  useEffect(() => { loadingRef.current = isLoading; }, [isLoading]);

  const stopMic = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  }, []);

  const handleMicClick = useCallback(() => {
    if (!SpeechRecognition) return;

    if (isListening) {
      stopMic();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = navigator.language || 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      if (loadingRef.current) return;
      const transcript = Array.from(event.results)
        .map(r => r[0].transcript)
        .join('');
      onChange({ target: { value: transcript } });
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.onerror = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
  }, [isListening, onChange, stopMic]);

  const handleSendMessage = useCallback(() => {
    stopMic();
    onSend();
  }, [onSend, stopMic]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      stopMic();
    }
    onKeyDown(e);
  }, [onKeyDown, stopMic]);

  return (
    <div className="chat-input-bar" ref={inputBarRef}>
      {suggestedAnswers && suggestedAnswers.length > 0 && (
        <div className="suggested-answers-input-bar">
          {suggestedAnswers.map((answer, index) => (
            <button
              key={index}
              type="button"
              className="suggested-answer-btn-input"
              onClick={() => onSuggestedAnswerClick && onSuggestedAnswerClick(answer)}
            >
              {answer}
            </button>
          ))}
        </div>
      )}
      <div className="chat-input-wrapper">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onKeyDown={handleKeyDown}
          placeholder={t('chat.placeholder')}
          className="chat-input-field"
          rows={1}
          maxLength={2000}
        />
        {value.length >= 2000 && <span className="chat-input-char-count">{value.length}/2000</span>}
        {SpeechRecognition && (
          <button
            type="button"
            className={`chat-input-icon-btn chat-input-mic ${isListening ? 'listening' : ''}`}
            onClick={handleMicClick}
            title={isListening ? 'Stop recording' : 'Voice input'}
          >
            <span className="material-symbols-outlined">
              {isListening ? 'mic' : 'mic_none'}
            </span>
          </button>
        )}
        <button
          type="button"
          className="chat-input-icon-btn chat-input-send"
          onClick={handleSendMessage}
          disabled={isLoading}
        >
          {isLoading ? (
            <svg className="send-icon spinning" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          ) : (
            <span className="material-symbols-outlined">send</span>
          )}
        </button>
      </div>
    </div>
  );
}
